// ECharts no React, com importação modular: só gauge, barra e o que eles usam (o pacote
// inteiro passa de 1 MB). Renderização em SVG — poucos elementos, texto nítido.
//
// O ECharts desenha com cores literais, não com as variáveis CSS do tema: quem monta a
// opção lê os tokens (lib/palette `token`) e o `useThemeVersion` faz a opção ser refeita
// quando o tema (claro/escuro ou a cor do grupo) muda.

import { BarChart, GaugeChart } from "echarts/charts";
import { GridComponent, MarkLineComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import type { EChartsCoreOption } from "echarts/core";
import { SVGRenderer } from "echarts/renderers";
import { useEffect, useRef, useState } from "react";

echarts.use([GaugeChart, BarChart, GridComponent, TooltipComponent, MarkLineComponent, SVGRenderer]);

export function EChart({ option, height, label, className }: { option: EChartsCoreOption; height: number; label: string; className?: string }) {
  const el = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const c = echarts.init(el.current!, undefined, { renderer: "svg" });
    chart.current = c;
    const ro = new ResizeObserver(() => c.resize());
    ro.observe(el.current!);
    return () => {
      ro.disconnect();
      c.dispose();
      chart.current = null;
    };
  }, []);

  // Mesma série a cada leitura: o ECharts anima o ponteiro do valor anterior ao novo.
  useEffect(() => {
    chart.current?.setOption(option);
  }, [option]);

  return <div ref={el} role="img" aria-label={label} className={className} style={{ height }} />;
}

/** Muda quando o tema muda: entra nas dependências de quem monta opções com cores. */
export function useThemeVersion(): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    const bump = () => setV((x) => x + 1);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", bump);
    // data-theme (claro/escuro forçado) e style (--brand do white label) ficam no <html>.
    const mo = new MutationObserver(bump);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "style", "class"] });
    return () => {
      mq.removeEventListener("change", bump);
      mo.disconnect();
    };
  }, []);
  return v;
}
